import React from 'react'
import { Label, Paragraph } from '@entur/typography'
import { GridItem } from '@entur/grid'
import { colors, space } from '@entur/tokens'
import { Tooltip } from '@entur/tooltip'
import { Badge } from '@entur/layout'
import { FormFieldTypes } from '../../utils/form-utils'
import { SensitiveInfo } from '../sensitive-info/sensitive-info'
import { useEmployee } from '../../contexts/employee-context'
import CopyButton from '../copy-button/copy-button'

type EmployeeInfoElementProps = {
  field: FormFieldTypes
  copyable?: boolean
}
export const EmployeeInfoElement: React.FC<EmployeeInfoElementProps> = ({ field, copyable = false }) => {
  const { employee } = useEmployee()
  if (field.locked) {
    return <SensitiveInfo field={field} />
  }

  const hasDraftChanges =
    employee.draftEmployee &&
    (field?.draftValue || '') !== field.value &&
    (field?.draftValue || '') !== field.displayValue

  if (hasDraftChanges) {
    return (
      <Tooltip content={`Nåværende verdi: ${field.displayValue || field.value || 'ingen'}`} placement="top">
        <div style={{ cursor: 'default' }}>
          <Label>{field.displayName}</Label>
          <Paragraph>
            {field.draftValue ? (
              <span style={{ borderBottom: `2px dotted ${colors.validation.mintContrast}` }}>
                {field.draftValue}
              </span>
            ) : (
              <span
                style={{
                  borderBottom: `2px dotted ${colors.validation.mintContrast}`,
                  width: '60%',
                  maxWidth: '90px',
                  display: 'inline-block',
                }}
              >
                &nbsp;
              </span>
            )}
            <Badge variant="success" style={{ display: 'inline', marginLeft: space.extraSmall2 }}>
              NY
            </Badge>
          </Paragraph>
        </div>
      </Tooltip>
    )
  }

  const value = (field.displayValue as string) || (field.value as string) || ''

  return (
    <>
      <Label>{field.displayName}</Label>
      <Paragraph>
        {value}
        {copyable && value && <CopyButton value={value} description={field.displayName} />}
      </Paragraph>
    </>
  )
}

export type GridSize = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12

export const infoElementInGridItem = (
  itemSize: GridSize,
  testName: string,
  field?: FormFieldTypes,
  copyable = false
): React.ReactNode => {
  if (itemSize === 0) {
    return <></>
  }
  return (
    <GridItem small={12} medium={itemSize} data-cy={testName}>
      {field && <EmployeeInfoElement field={field} copyable={copyable} />}
    </GridItem>
  )
}
